import { transformDateToLocalDateString } from "helpers";
import { NotificationLog } from "services/api/notifications/types";
import { CHANNEL_BADGE_MAP } from "services/api/notifications/consts";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import { Badge } from "ui/components/Badge";
import VehicleCell from "ui/components/Table/TableSearch/components/Cells/VehicleCell";
import Tooltip from "ui/components/Tooltip/Tooltip";

/** Datum i vrijeme — u zapisniku je bitno i kada je točno poslano, ne samo kojeg dana. */
const formatSentAt = (value: string) => {
  const date = new Date(value);
  const time = date.toLocaleTimeString("hr-HR", { hour: "2-digit", minute: "2-digit" });

  return `${transformDateToLocalDateString(date)} ${time}`;
};

export const NOTIFICATION_LOG_COLUMNS = (
  t: (key: string) => string,
  onVehicleClick: (e: React.MouseEvent<HTMLDivElement>, vehicleId: string) => void
): Column[] => [
  {
    title: "Vozilo",
    field: "",
    type: ColumnTypes.custom,
    render: (row: NotificationLog) => {
      // Prilagođene obavijesti nisu vezane uz vozilo, pa im ćelija ostaje prazna.
      if (!row.vehicle) return null;

      return (
        <VehicleCell
          vehicle={row.vehicle}
          onClick={(e) => onVehicleClick(e, row.vehicle!.id)}
        />
      );
    },
  },
  {
    title: t("admin.notification.log.definitionTitle"),
    type: ColumnTypes.custom,
    render: (row: NotificationLog) => (
      <div className="max-w-lg">
        <div className="text-md">{row.definitionTitle ?? row.sentTitle}</div>
        <div className="text-sm font-light text-slate-500 truncate">
          {row.entityTypeName}
        </div>
      </div>
    ),
  },
  {
    title: t("admin.notification.log.entityName"),
    field: "entityDisplayName",
    type: ColumnTypes.string,
  },
  {
    title: t("admin.notification.log.channel"),
    type: ColumnTypes.custom,
    render: (row: NotificationLog) => (
      <Badge
        text={row.channelName}
        variant={CHANNEL_BADGE_MAP[row.channelName]?.variant ?? "info"}
        height="small"
        showDot
      />
    ),
  },
  {
    title: t("admin.notification.log.status"),
    type: ColumnTypes.custom,
    render: (row: NotificationLog) => {
      const badge = (
        <Badge
          text={
            row.isSuccess
              ? t("admin.notification.log.success")
              : t("admin.notification.log.failed")
          }
          variant={row.isSuccess ? "success" : "danger"}
          height="small"
          showDot
        />
      );

      // Razlog neuspjeha se ne da smjestiti u redak, a bez njega je "Neuspjelo" beskorisno.
      if (row.isSuccess || !row.errorMessage) return badge;

      return (
        <Tooltip
          position="top-right"
          size="medium"
          trigger={<span className="cursor-help">{badge}</span>}
        >
          <div className="text-sm leading-relaxed">{row.errorMessage}</div>
        </Tooltip>
      );
    },
  },
  {
    title: t("admin.notification.log.sentTries"),
    field: "sentTries",
    type: ColumnTypes.string,
  },
  {
    title: t("admin.notification.log.sentOn"),
    type: ColumnTypes.custom,
    render: (row: NotificationLog) => (
      <div className="text-md">{row.sentOnUtc ? formatSentAt(row.sentOnUtc) : "-"}</div>
    ),
  },
  {
    title: t("admin.notification.log.created"),
    type: ColumnTypes.custom,
    render: (row: NotificationLog) => (
      <div className="text-md">{formatSentAt(row.created)}</div>
    ),
  },
];
